import {
  createResourceAtom,
  createResourceCacheMapAtom,
  createSingleCacheEntryAtom,
  lazyLoadableAtom,
} from 'lib/atoms';
import { atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';
import request from 'lib/request';
import { useMemo } from 'react';
import {
  SlotNRIResponse,
  CRMTractsAndSubtractsResult,
  SlotSummaryResponse,
  AvailableForecastsResponse,
  AvailableAfesResponse,
} from 'types/api-responses';
import {
  mapSlotNRIResponseToTractNRI,
  mapSlotNRIResponseToSlotNRI,
} from 'utils/misc';
import {
  PackageCashflowsResponse,
  PackageEvaluationAllocationResponse,
  PackageEvaluationAssetsResponse,
  PackageEvaluationOverviewCombinedResponse,
  PackageEvaluationOverviewResponse,
  PackageEvaluationResponse,
  PackageEvaluationUnitsResponse,
  PackageDiscountRatesResponse,
  PackageEvaluationValuationOverviewResponse,
  PackageSlotsPerUnitAllocationResponse,
  PackageEvaluationInventoryAssetsResponse,
  PackageEvaluationWellsResponse,
  PackageEvaluationUnassignedWellsResponse,
  PackageEvaluationDetailsDownloads,
  PackageSlotsResponse,
  BlobArtifact,
} from 'types/packages-api/responses';
import { wellStatusCounts } from 'utils/slot-categories';

export type { BlobArtifact };

const evaluationPackageOverviewCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationOverviewResponse>();

const evaluationPackageOverviewDiscountRatesCacheAtom =
  createResourceCacheMapAtom<PackageDiscountRatesResponse>();

const evaluationPackageOverviewCombinedCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationOverviewCombinedResponse>();

const evaluationPackageValuationOverviewCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationValuationOverviewResponse>();

const evaluationAllocationsCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationAllocationResponse[]>();

const evaluationUnitsCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationUnitsResponse[]>();

const evaluationAssetsCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationAssetsResponse[]>();

const evaluationInventoryAssetsCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationInventoryAssetsResponse[]>();

const evaluationWellsCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationWellsResponse>();

const partiallyCoveredWellsCacheAtom =
  createResourceCacheMapAtom<PackageEvaluationUnassignedWellsResponse>();

const blobArtifactsCacheAtom = createResourceCacheMapAtom<BlobArtifact[]>();

const evaluationsCashflowsCacheAtom =
  createResourceCacheMapAtom<PackageCashflowsResponse>();

const usePackageEvaluation = (href: string) =>
  useMemo(() => createResourceAtom<PackageEvaluationResponse>(href), [href]);

const useCreateEvaluationAllocationsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(evaluationAllocationsCacheAtom)(href),
    [href]
  );

const useCreateEvaluationUnitsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(evaluationUnitsCacheAtom)(href),
    [href]
  );

const useCreateEvaluationAssetsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(evaluationAssetsCacheAtom)(href),
    [href]
  );

const useCreateEvaluationInventoryAssetsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(evaluationInventoryAssetsCacheAtom)(href),
    [href]
  );

const useCreateEvaluationWellsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(evaluationWellsCacheAtom)(href),
    [href]
  );

const useCreatePartiallyCoveredWellsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(partiallyCoveredWellsCacheAtom)(href),
    [href]
  );

const useCreateBlobArtifactsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(blobArtifactsCacheAtom)(href),
    [href]
  );

const useCreateEvaluationPackageOverviewAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(evaluationPackageOverviewCacheAtom)(href),
    [href]
  );

const useCreateEvaluationPackageOverviewCombinedAtom = (href: string) =>
  useMemo(
    () =>
      createSingleCacheEntryAtom(evaluationPackageOverviewCombinedCacheAtom)(
        href
      ),
    [href]
  );

const useCreateDiscountRatesAtom = (href: string) =>
  useMemo(
    () =>
      createSingleCacheEntryAtom<PackageDiscountRatesResponse>(
        evaluationPackageOverviewDiscountRatesCacheAtom
      )(href),
    [href]
  );

const useCreateEvaluationPackageValuationOverviewAtom = (href: string) =>
  useMemo(
    () =>
      createSingleCacheEntryAtom(evaluationPackageValuationOverviewCacheAtom)(
        href
      ),
    [href]
  );

const useCreateEvaluationPackageCashflowsAtom = (href: string) =>
  useMemo(
    () => createSingleCacheEntryAtom(evaluationsCashflowsCacheAtom)(href),
    [href]
  );

const useSlotsAtom = (href: string) => {
  return useMemo(
    () => createResourceAtom<PackageSlotsPerUnitAllocationResponse>(href),
    [href]
  );
};

const usePackageSlotsAtom = (href: string) => {
  return useMemo(() => createResourceAtom<PackageSlotsResponse>(href), [href]);
};

const useEvaluationDownloadsAtom = (href: string) =>
  useMemo(
    () =>
      createResourceAtom<PackageEvaluationDetailsDownloads[]>(
        href,
        'keep-old-data-on-refetch'
      ),
    [href]
  );

// Fetches slot allocations data from optimized /slot_allocations endpoint (with tracts)
const packageSlotAllocationsDataAtom = atomFamily((packageId: number) =>
  atom(async () => {
    const response = await request.get<SlotNRIResponse>(
      `/packages/${packageId}/slot_allocations`
    );
    if ('error' in response) {
      throw new Error(response.error ?? 'Error loading slot allocations');
    }
    return response;
  })
);

// Shared: transforms to TractNRI format (no loadable)
const packageTractNriDataAtom = atomFamily((packageId: number) =>
  atom(async (get) => {
    const response = await get(packageSlotAllocationsDataAtom(packageId));
    return mapSlotNRIResponseToTractNRI(response);
  })
);

// Slot allocations tab - wraps shared atom in loadable for UI
const packageTractNriAtom = atomFamily((packageId: number) =>
  loadable(packageTractNriDataAtom(packageId))
);

// Slots tab - uses optimized /slot_allocations endpoint
const packageSlotNriAtom = atomFamily((packageId: number) =>
  loadable(
    atom(async (get) => {
      const response = await get(packageSlotAllocationsDataAtom(packageId));
      return mapSlotNRIResponseToSlotNRI(response);
    })
  )
);

interface CrmTractsKey {
  packageId: number;
  evaluationCalculationId?: number | null;
}

const crmTractsKeyEqual = (a: CrmTractsKey, b: CrmTractsKey) =>
  a.packageId === b.packageId &&
  a.evaluationCalculationId === b.evaluationCalculationId;

const packageCrmTractsAtom = atomFamily(
  ({ packageId, evaluationCalculationId }: CrmTractsKey) =>
    loadable(
      atom(async () => {
        const url = evaluationCalculationId
          ? `/packages/${packageId}/crm_tracts_and_subtracts?evaluation_calculation_id=${evaluationCalculationId}`
          : `/packages/${packageId}/crm_tracts_and_subtracts`;
        const response = await request.post<
          CRMTractsAndSubtractsResult,
          { error: string }
        >(url, null, new AbortController());
        if (response.type !== 'success') {
          throw new Error('Error loading CRM tracts');
        }
        return response.data;
      })
    ),
  crmTractsKeyEqual
);

// Base atom - fetches and caches raw slot_summary API response
const packageSlotSummaryResponseAtom = atomFamily((packageId: number) =>
  atom(async () => {
    const response = await request.get<SlotSummaryResponse>(
      `/packages/${packageId}/slot_summary`
    );
    if ('error' in response) {
      throw new Error(response.error ?? 'Error loading slot summary');
    }
    return response;
  })
);

// Derived atom for slots by formation
const packageSlotsByFormationAtom = atomFamily((packageId: number) =>
  loadable(
    atom(async (get) => {
      const response = await get(packageSlotSummaryResponseAtom(packageId));
      return response.slotsByFormation;
    })
  )
);

// Base slot summary atom - returns rows with backend-provided verification counts
// Used with SlotAllocationsSummary/SlotsSummary wrappers
const packageSlotSummaryAtom = atomFamily((packageId: number) =>
  loadable(
    atom(async (get) => {
      const response = await get(packageSlotSummaryResponseAtom(packageId));
      return (
        response.slotsSummary?.rows?.map((row) => ({
          wellType: row.unitType,
          producing: row.pdp,
          duc: row.duc,
          permit: row.permit,
          ltd: row.ltd,
          shutIn: row.shutIn,
          inactive: row.inactive,
          ...wellStatusCounts(row),
          expiredPermit: row.expiredPermit,
          wellboreExcluded: row.wellboreExcluded ?? 0,
          total: row.total,
          // Backend-provided verification counts (camelCase from API)
          slotVerification: row.slotVerification,
          nriVerification: row.nriVerification,
          // Excluded verification counts (LTD + Expired Permit) for toggle filtering
          excludedSlotVerification: row.excludedSlotVerification,
          excludedNriVerification: row.excludedNriVerification,
        })) ?? []
      );
    })
  )
);

// Combined atom for Slots tab - fetches both slot NRI and slot summary together
const packageSlotSummaryDataAtom = atomFamily((packageId: number) =>
  loadable(
    atom(async (get) => {
      const [slotAllocationsResponse, slotSummaryResponse] = await Promise.all([
        get(packageSlotAllocationsDataAtom(packageId)),
        get(packageSlotSummaryResponseAtom(packageId)),
      ]);

      const slots = mapSlotNRIResponseToSlotNRI(slotAllocationsResponse);
      const slotSummary =
        slotSummaryResponse.slotsSummary?.rows?.map((row) => ({
          wellType: row.unitType,
          producing: row.pdp,
          duc: row.duc,
          permit: row.permit,
          ltd: row.ltd,
          shutIn: row.shutIn,
          inactive: row.inactive,
          ...wellStatusCounts(row),
          expiredPermit: row.expiredPermit,
          wellboreExcluded: row.wellboreExcluded ?? 0,
          total: row.total,
        })) ?? [];

      return { slots, slotSummary };
    })
  )
);

// ============================================================
// Available Forecasts (Edit Volume Source Modal)
// ============================================================

/**
 * Key for the Edit Volume Source modal's candidate list.
 */
interface AvailableForecastsKey {
  slotId: string;
  packageId: number;
  currentVolumeSourceId?: string;
  typeCurveAreaName?: string;
}

/**
 * Equality for AvailableForecastsKey, so atomFamily reuses one atom per distinct request.
 * @param {AvailableForecastsKey} a - First key.
 * @param {AvailableForecastsKey} b - Second key.
 * @return {boolean} True when the keys name the same request.
 */
const availableForecastsKeyEqual = (
  a: AvailableForecastsKey,
  b: AvailableForecastsKey
): boolean =>
  a.slotId === b.slotId &&
  a.packageId === b.packageId &&
  a.currentVolumeSourceId === b.currentVolumeSourceId &&
  a.typeCurveAreaName === b.typeCurveAreaName;

/**
 * Available forecasts atom using lazyLoadableAtom pattern.
 * Only fetches when triggered, can be re-triggered on error.
 * Returns AvailableForecastsResponse with pdpDeclines and typeCurves arrays.
 *
 * Sends `packageId`, so the server answers from the evaluation's Type Curve Scenario: the
 * curves the eval can resolve and price, natively built ones included, the same list Eval 3.0's
 * picker offers. Without it the server falls back to legacy TypeCurve::Header rows, which hides
 * every curve built in a Type Curve workspace. `currentVolumeSourceId` marks the row's own
 * curve as current. `typeCurveAreaName` narrows the list to the row's own area, as Eval 3.0
 * does: an LTD slot has no well to narrow by, and without it would be offered every curve in
 * the package. No formation is sent: the server narrows by formation only together with a unit
 * id, which these rows do not carry, so the list covers the row's whole area.
 */
const availableForecastsAtom = atomFamily(
  ({
    slotId,
    packageId,
    currentVolumeSourceId,
    typeCurveAreaName,
  }: AvailableForecastsKey) =>
    lazyLoadableAtom(async () => {
      const params = new URLSearchParams({ packageId: String(packageId) });
      if (currentVolumeSourceId) {
        params.set('currentVolumeSourceId', currentVolumeSourceId);
      }
      if (typeCurveAreaName) {
        params.set('typeCurveAreaName', typeCurveAreaName);
      }
      const response = await request.get<AvailableForecastsResponse>(
        `/slots/${slotId}/available_forecasts?${params.toString()}`
      );
      if ('error' in response) {
        throw new Error(response.error ?? 'Failed to load available forecasts');
      }
      return response;
    }),
  availableForecastsKeyEqual
);

// ============================================================
// Available AFEs (Edit AFE Source Modal)
// ============================================================

/**
 * Key for available AFEs atomFamily using composite key pattern.
 */
interface AvailableAfesKey {
  slotId: string;
}

const availableAfesKeyEqual = (
  a: AvailableAfesKey,
  b: AvailableAfesKey
): boolean => a.slotId === b.slotId;

/**
 * Available AFEs atom using lazyLoadableAtom pattern.
 * Only fetches when triggered, can be re-triggered on error.
 * Returns AvailableAfesResponse with availableAfes array.
 */
const availableAfesAtom = atomFamily(
  ({ slotId }: AvailableAfesKey) =>
    lazyLoadableAtom(async () => {
      const response = await request.get<AvailableAfesResponse>(
        `/slots/${slotId}/available_afes`
      );
      if ('error' in response) {
        throw new Error(response.error ?? 'Failed to load available AFEs');
      }
      return response;
    }),
  availableAfesKeyEqual
);

/**
 * Bulk-trigger lazy fetch for {@link availableAfesAtom}. Walks the given
 * slot ids, reads each slot's loadable state, and re-triggers the fetch on
 * any idle or errored entry. Idempotent for slots that are loading or have
 * data — keeps the imperative store access encapsulated here instead of
 * leaking into hooks.
 */
const triggerBulkAvailableAfesFetchAtom = atom(
  null,
  (get, set, slotIds: string[]) => {
    for (const slotId of slotIds) {
      const slotAtom = availableAfesAtom({ slotId });
      const current = get(slotAtom);
      const isIdle = current.state === 'hasData' && current.data === null;
      const isError = current.state === 'hasError';
      if (isIdle || isError) set(slotAtom);
    }
  }
);

export {
  availableAfesAtom,
  triggerBulkAvailableAfesFetchAtom,
  availableForecastsAtom,
  packageCrmTractsAtom,
  packageSlotSummaryAtom,
  packageSlotsByFormationAtom,
  packageSlotSummaryDataAtom,
  packageTractNriAtom,
  packageSlotNriAtom,
  useCreateDiscountRatesAtom,
  useCreateEvaluationAllocationsAtom,
  useCreateEvaluationAssetsAtom,
  useCreateEvaluationInventoryAssetsAtom,
  useCreateEvaluationPackageCashflowsAtom,
  useCreateEvaluationPackageOverviewAtom,
  useCreateEvaluationPackageOverviewCombinedAtom,
  useCreateEvaluationPackageValuationOverviewAtom,
  useCreateEvaluationUnitsAtom,
  useCreatePartiallyCoveredWellsAtom,
  useCreateBlobArtifactsAtom,
  useCreateEvaluationWellsAtom,
  useEvaluationDownloadsAtom,
  usePackageEvaluation,
  useSlotsAtom,
  usePackageSlotsAtom,
};
